import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '../store';

// Session is considered idle after WARN_MS of no user activity, at which
// point a countdown warning is shown. If still idle by LOGOUT_MS (measured
// from the same last-activity point), the user is signed out automatically.
const WARN_MS = 25 * 60 * 1000;
const LOGOUT_MS = 30 * 60 * 1000;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

const formatCountdown = (ms) => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

export default function IdleLogoutModal() {
  const { logout } = useStore();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [remainingMs, setRemainingMs] = useState(LOGOUT_MS - WARN_MS);
  const lastActivityRef = useRef(Date.now());
  const warnTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const tickRef = useRef(null);

  const handleLogout = useCallback(async () => {
    setShowWarning(false);
    await logout();
    navigate('/login');
  }, [logout, navigate]);

  const clearTimers = () => {
    clearTimeout(warnTimerRef.current);
    clearTimeout(logoutTimerRef.current);
    clearInterval(tickRef.current);
  };

  const scheduleTimers = useCallback(() => {
    clearTimers();
    warnTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setRemainingMs(LOGOUT_MS - WARN_MS);
      tickRef.current = setInterval(() => {
        const left = LOGOUT_MS - (Date.now() - lastActivityRef.current);
        setRemainingMs(left);
      }, 1000);
    }, WARN_MS);
    logoutTimerRef.current = setTimeout(handleLogout, LOGOUT_MS);
  }, [handleLogout]);

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);
    scheduleTimers();
  }, [scheduleTimers]);

  useEffect(() => {
    scheduleTimers();
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, resetActivity));
    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, resetActivity));
    };
  }, [resetActivity, scheduleTimers]);

  if (!showWarning) return null;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 380, textAlign: 'center' }}>
        <div className="modal-body" style={{ padding: '20px 24px 8px' }}>
          <div style={{ fontSize: '1.75rem', marginBottom: 6 }}>⏱️</div>
          <h3 style={{ marginBottom: 6, fontSize: '1.0625rem' }}>Still there?</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem', marginBottom: 2 }}>
            You've been inactive for a while. For your security, you'll be signed out automatically in
          </p>
          <p style={{ fontSize: '1.375rem', fontWeight: 800, color: 'var(--brand-violet)', margin: '4px 0 2px' }}>
            {formatCountdown(remainingMs)}
          </p>
        </div>
        <div className="modal-footer" style={{ justifyContent: 'center', padding: '12px 24px 20px' }}>
          <button className="btn btn-secondary" onClick={handleLogout}>Sign out now</button>
          <button className="btn btn-primary" onClick={resetActivity}>Stay signed in</button>
        </div>
      </div>
    </div>
  );
}
